import * as React from 'react';
import { TableRow } from '@bb-ui/react-library/dist/components/TableRow';
import { TableCell } from '@bb-ui/react-library/dist/components/TableCell';
import { IconButton, Tooltip, Chip, makeStyles, createStyles, Theme } from '@bb-ui/react-library';
import { ContentView } from '@bb-ui/icons/dist/medium';
import moment from 'moment';
import { Link, useLocation } from 'react-router-dom';
import path from 'path';
import { IJobPreview } from 'App.types';
import { useTranslation } from 'react-i18next';
import { JobExecution, Status } from '@lct-jobs/core';
import { JobStatusIcon } from '../JobStatusIcon/JobStatusIcon';
import {
  MassOperationType,
  getMassOperationName,
  getMassOperationType,
} from '../JobExecution/massOperationType';

export interface JobExecutionRowProps {
  jobExecution: JobExecution;
  detailPath?: string;
  showOperationType?: boolean;
  showUser?: boolean;
}

const LooseIconButton = IconButton as any;

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    previewCellTableCell: {
      display: 'flex',
      flexDirection: 'column',
    },
    previewCellTd: {
      fontWeight: 'bold',
    },
    noPreview: {
      color: theme.palette.text.secondary,
    },
  }),
);

export const JobExecutionRow: React.FC<JobExecutionRowProps> = (props) => (
  <TableRow>
    <TableCell>{getMassOperationName(props.jobExecution) ?? props.jobExecution.jobName}</TableCell>
    {props.showOperationType && <JobExecutionTypeCell jobExecution={props.jobExecution} />}
    <JobExecutionStatusCell status={props.jobExecution.status} />
    <JobExecutionDateCell date={props.jobExecution.startDate} />
    <JobExecutionDateCell date={props.jobExecution.endDate} />
    {props.showUser && <TableCell>{props.jobExecution.user}</TableCell>}
    <JobExecutionDetailsCell
      executionId={props.jobExecution.executionId}
      tenantId={props.jobExecution.tenantId}
      execution={props.jobExecution}
      detailPath={props.detailPath}
    />
  </TableRow>
);

export const JobExecutionTypeCell: React.FC<{ jobExecution: JobExecution }> = (props) => {
  const { t } = useTranslation();
  const type = getMassOperationType(props.jobExecution);
  if (!type) {
    return <TableCell />;
  }
  return (
    <TableCell>
      {type === MassOperationType.MASS_JOB
        ? t('massQueryTooling.operationType.massJob')
        : t('massQueryTooling.operationType.massQuery')}
    </TableCell>
  );
};

export const JobExecutionDateCell: React.FC<{ date?: string }> = (props) => {
  if (!props.date) {
    return <TableCell />;
  }
  const date = moment(props.date);
  return <TableCell>{date.format('lll')}</TableCell>;
};

export const JobExecutionStatusCell: React.FC<{ status: Status }> = (props) => (
  <TableCell>
    <Tooltip title={props.status}>
      <JobStatusIcon style={{ paddingLeft: '8px' }} status={props.status} />
    </Tooltip>
  </TableCell>
);

export const JobExecutionDetailsCell: React.FC<{
  executionId: string;
  tenantId: string;
  execution: JobExecution;
  detailPath?: string;
}> = (props) => {
  const location = useLocation();
  const linkPath = props.detailPath ?? path.join(location.pathname, props.executionId);
  return (
    <TableCell>
      <LooseIconButton
        component={Link}
        to={{
          pathname: linkPath,
          state: { executionTenantId: props.execution?.tenantId ?? props.tenantId },
        }}
      >
        <ContentView />
      </LooseIconButton>
    </TableCell>
  );
};

export const JobExecutionPreviewCell: React.FC<{ preview: IJobPreview }> = (props) => {
  const classes = useStyles(props);

  return (
    <TableCell className={classes.previewCellTableCell}>
      <td className={classes.previewCellTd}>{props.preview.title}</td>
      <td>{props.preview.contentPath}</td>
      {props.preview.chipText && (
        <td className={classes.previewCellTd}>{props.preview.chipText}</td>
      )}
      {props.preview.chipLinkPath && (
        <td>
          <Chip label={props.preview.chipLinkPath} />
        </td>
      )}
      {props.preview?.chipTenantIdLinkPath && (
        <td>
          <Chip label={props.preview.chipTenantIdLinkPath} />
        </td>
      )}
    </TableCell>
  );
};
